import { useEntitlements as usePackageEntitlements } from '@parcel2go/npm-p2g-app';
import type { UseEntitlementsResult } from '@parcel2go/npm-p2g-app';
import { useEffect, useMemo } from 'react';
import useSubscriptionsEnabled from './use-subscriptions-enabled';
import { settleEntitlementsSync } from '~/apps/subscriptions/pending-entitlements-sync';

export const EntitlementKeys = {
  SERVICE_ANALYTICS: 'service_analytics'
};

/**
 * The only call site of the package's `useEntitlements` in this app - import THIS, never the
 * package hook directly.
 *
 * The `enabled` gate is not decoration: without it the hook issues an account-scoped request on
 * every page for every signed-in customer of every tenant, including the ones that sell no
 * subscriptions and have no `paymentsApi` configured, which is also one Sentry warning per page
 * load forever. The result is returned unnarrowed so callers keep `subscription`, `status`,
 * `billingDetailUnavailable`, `sync()` and the rest.
 */
export function useEntitlements(): UseEntitlementsResult {
  const enabled = useSubscriptionsEnabled();
  const result = usePackageEntitlements({ enabled });

  // Settles a sync flagged by an onboarding subscribe (pending-entitlements-sync.ts). No-op unless
  // one is pending.
  useEffect(() => {
    if (enabled) {
      settleEntitlementsSync(result.sync);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return result;
}

/**
 * Whether the customer's subscription grants a boolean capability.
 *
 * Reads `values` directly rather than the package's `has()`/`get()`. The live payload carries
 * boolean capabilities as the literal string `'true'` alongside parameter-valued ones
 * (`prepay_bonus: '2.5'`) in the same bag, so:
 *   - `has()` answers key PRESENCE and cannot tell `'true'` from `'false'`;
 *   - `get()` inspects only the FIRST entitlement carrying the key, missing a second grant source.
 * Those helpers are also conveniences over the wire and may be retuned as the backend model
 * settles, whereas `entitlements` is the payload itself.
 *
 * A capability lives inside `values` keyed by its own name (quoting-api#1511); `key`/`name` are
 * tier labels (e.g. 'prepay_bonus_t1') and are never branched on.
 */
export function useHasEntitlement(key: string): boolean {
  const { entitlements } = useEntitlements();
  return useMemo(() => entitlements.some((e) => e.values?.[key] === 'true'), [entitlements, key]);
}

export { KnownEntitlementKeys, EntitlementsError } from '@parcel2go/npm-p2g-app';
export type { UseEntitlementsResult, EntitlementsStatus } from '@parcel2go/npm-p2g-app';
